import { useMutation, useMutationState } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { cn } from '@/lib/utils';
import { inElectron } from '@/utils/capabilities';

import { SettingsSection } from '../settings-section';

import {
  CHATGPT_IMPORT_MUTATION_KEY,
  formatImportSummary,
  useChatGptImportMutation,
  useChatGptImportStatusQuery,
} from './chatgpt-import/chatgpt-import.query';

const formatFileSize = (bytes: number) =>
  bytes >= 1_000_000
    ? `${(bytes / 1_000_000).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1000))} KB`;

const isCsvFile = (file: File) => file.name.toLowerCase().endsWith('.csv');

const isZipFile = (file: File) => file.name.toLowerCase().endsWith('.zip');

export const ImportSettings = observer(() => {
  const hasFeature = useFeature();
  const showChatGptImport = inElectron() && hasFeature(FEATURES.CHATGPT_EXPORT);

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Import</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <p className="text-foreground text-sm">Import meetings from Granola</p>
        <MeetingsCsvImport />
      </SettingsSection.Group>
      {showChatGptImport && (
        <SettingsSection.Group>
          <p className="text-foreground text-sm">Import chats from ChatGPT</p>
          <ChatGptImport />
        </SettingsSection.Group>
      )}
    </SettingsSection.Root>
  );
});

const MeetingsCsvImport = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { mutate: importCsv, isPending } = useMutation({
    mutationKey: ['import-granola-csv'],
    mutationFn: (file: File) => backendApis.meetingNotes.importGranolaCsv(file),
    onSuccess: () => {
      setSelectedFile(null);
      toast.success('Granola import queued', {
        description: 'Your meetings will appear shortly.',
      });
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: { data?: { detail?: { user_error_message?: string } } };
      };
      toast.error('Failed to import Granola CSV', {
        description:
          apiError.response?.data?.detail?.user_error_message ??
          "We couldn't import your Granola CSV. Please try again, and check the file is an unmodified Granola history export.",
      });
    },
  });

  const selectFirstFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    if (!isCsvFile(file)) {
      toast.error('Choose a CSV file', {
        description: 'Granola exports are provided as .csv files.',
      });
      return;
    }

    setSelectedFile(file);
  };

  return (
    <YStack className="gap-3">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(event) => {
          selectFirstFile(event.target.files);
          event.target.value = '';
        }}
      />

      {selectedFile ? (
        <SelectedFile
          name={selectedFile.name}
          detail={formatFileSize(selectedFile.size)}
          importLabel="Import meetings"
          isImporting={isPending}
          onCancel={() => setSelectedFile(null)}
          onImport={() => importCsv(selectedFile)}
        />
      ) : (
        <Dropzone
          title="Drag and drop your meetings CSV"
          subtitle="or choose a file from your computer"
          buttonLabel="Choose CSV file"
          testId="granola-csv-dropzone"
          onChoose={() => fileInputRef.current?.click()}
          onFiles={selectFirstFile}
        />
      )}

      <ExportHelp
        title="How to export from Granola"
        steps={[
          <>
            Open Granola and go to <strong>Settings</strong>
          </>,
          <>
            Select <strong>Profile</strong> under Preferences
          </>,
          <>
            Click <strong>Generate CSV</strong>
          </>,
          'Select the workspaces you want to include',
          'Granola will email you the CSV when it’s ready',
        ]}
      />
    </YStack>
  );
};

const ChatGptImport = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: importStatus } = useChatGptImportStatusQuery();
  const { mutate: importExport, variables: lastFile } =
    useChatGptImportMutation();
  const uploadingFileNames = useMutationState({
    filters: { mutationKey: CHATGPT_IMPORT_MUTATION_KEY, status: 'pending' },
    select: (mutation) => (mutation.state.variables as File).name,
  });
  const isImporting =
    uploadingFileNames.length > 0 || importStatus?.status === 'running';

  const selectFirstFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    if (!isZipFile(file)) {
      toast.error('Choose the .zip file', {
        description:
          'Drop the .zip ChatGPT emailed you, not the unzipped folder or a file from inside it.',
      });
      return;
    }

    setSelectedFile(file);
  };

  return (
    <YStack className="gap-3">
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        onChange={(event) => {
          selectFirstFile(event.target.files);
          event.target.value = '';
        }}
      />

      {selectedFile || isImporting ? (
        <SelectedFile
          name={
            selectedFile?.name ??
            uploadingFileNames[0] ??
            lastFile?.name ??
            'ChatGPT export'
          }
          detail={
            selectedFile ? formatFileSize(selectedFile.size) : 'Importing…'
          }
          importLabel="Import chats"
          isImporting={isImporting}
          onCancel={() => setSelectedFile(null)}
          onImport={() => {
            if (!selectedFile) return;
            importExport(selectedFile, {
              onSuccess: () => setSelectedFile(null),
            });
          }}
        />
      ) : (
        <Dropzone
          title="Drag and drop your ChatGPT export"
          subtitle="or choose the .zip file from your computer"
          buttonLabel="Choose ZIP file"
          testId="chatgpt-zip-dropzone"
          onChoose={() => fileInputRef.current?.click()}
          onFiles={selectFirstFile}
        />
      )}

      {!selectedFile &&
        !isImporting &&
        importStatus?.status === 'completed' && (
          <p className="text-foreground-muted text-xs">
            Last import: {formatImportSummary(importStatus.result)}
          </p>
        )}

      <ExportHelp
        title="How to export from ChatGPT"
        steps={[
          <>
            In ChatGPT, open <strong>Settings</strong> →{' '}
            <strong>Data controls</strong>
          </>,
          <>
            Click <strong>Export data</strong>, then{' '}
            <strong>Confirm export</strong>
          </>,
          'Wait for the email from ChatGPT (it can take up to a couple of days)',
          'Download the .zip from that email (the link expires after 24 hours)',
          'Drop the .zip here without unzipping it',
        ]}
      />
    </YStack>
  );
};

type DropzoneProps = {
  title: string;
  subtitle: string;
  buttonLabel: string;
  testId: string;
  onChoose: () => void;
  onFiles: (files: FileList | null) => void;
};

const Dropzone = ({
  title,
  subtitle,
  buttonLabel,
  testId,
  onChoose,
  onFiles,
}: DropzoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  return (
    <YStack
      center
      data-testid={testId}
      className={cn(
        'gap-5 rounded-lg border border-dashed border-border-strongest bg-surface px-6 py-10 text-center',
        isDragging && 'border-accent bg-surface-active'
      )}
      onDragEnter={() => {
        dragDepth.current += 1;
        setIsDragging(true);
      }}
      onDragLeave={() => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setIsDragging(false);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        dragDepth.current = 0;
        setIsDragging(false);
        onFiles(event.dataTransfer.files);
      }}
    >
      <YStack center className="gap-1">
        <YStack
          center
          className="size-12 rounded-full border border-border bg-surface"
        >
          <Icons.upload className="size-5 text-foreground-muted" />
        </YStack>
        <p className="text-foreground mt-2 text-sm">{title}</p>
        <p className="text-foreground-muted text-xs">{subtitle}</p>
      </YStack>
      <Button variant="default" size="sm" onClick={onChoose}>
        {buttonLabel}
      </Button>
    </YStack>
  );
};

type SelectedFileProps = {
  name: string;
  detail: string;
  importLabel: string;
  isImporting: boolean;
  onCancel: () => void;
  onImport: () => void;
};

const SelectedFile = ({
  name,
  detail,
  importLabel,
  isImporting,
  onCancel,
  onImport,
}: SelectedFileProps) => {
  return (
    <YStack
      center
      className="gap-5 rounded-lg border border-border-strongest bg-surface px-6 py-10 text-center"
    >
      <YStack center className="w-full gap-1">
        <YStack
          center
          className="size-12 rounded-full border border-border bg-surface"
        >
          <Icons.textFile className="size-5 text-foreground-muted" />
        </YStack>
        <p
          className="text-foreground mt-2 max-w-full truncate text-sm"
          title={name}
        >
          {name}
        </p>
        <p className="text-foreground-muted text-xs">{detail}</p>
      </YStack>

      <XStack className="gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={isImporting}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          isLoading={isImporting}
          onClick={onImport}
        >
          {importLabel}
        </Button>
      </XStack>
    </YStack>
  );
};

type ExportHelpProps = {
  title: string;
  steps: ReactNode[];
};

const ExportHelp = ({ title, steps }: ExportHelpProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <YStack className="overflow-hidden rounded-lg border border-border bg-surface">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="flex items-center gap-2 px-3 py-2.5 text-left hover:bg-surface-hover"
      >
        <Icons.info className="size-4 shrink-0 text-foreground-muted" />
        <span className="text-foreground text-sm">{title}</span>
        <span className="flex-1" />
        <Icons.chevronDown
          className={cn(
            'size-4 shrink-0 text-foreground-subtle',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <YStack className="border-t border-border px-3 py-3">
          <ol className="list-decimal space-y-2.5 pl-5 text-xs text-foreground-muted">
            {steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </YStack>
      )}
    </YStack>
  );
};
